import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Image,
  SimpleGrid,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Input,
  Spinner,
  Wrap,
  WrapItem,
  Stack,
  HStack,
  Tooltip,
} from "@chakra-ui/react";
import UuidPreview from "./UuidPreview";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelect from "./CodesetSelect";
import { useClocks } from "./hooks/useClocks";
import ClockCard from "./components/ClockCard";
import GRLCPlacementEditor from "./GRLCPlacementEditor";
import DealEditor from "./DealEditor";
import PlacingStepper from "./PlacingStepper";
import {
  HiOutlineCode,
  HiOutlineArrowNarrowLeft,
  HiOutlineDocumentText,
  HiOutlineMenu,
  HiOutlineStatusOnline,
  HiOutlineCog,
  HiOutlinePlus,
  HiOutlineTrash,
  HiX,
} from "react-icons/hi";

const ProductHomepage = () => {
  const { productCode } = useParams();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [product, setProduct] = useState(null);
  const { clocks, loading: clocksLoading } = useClocks();
  const [selectedGRLCPlacement, setSelectedGRLCPlacement] = useState(null);
  const [selectedDealInstance, setSelectedDealInstance] =
    useState(null);

  const {
    isOpen: isEditorOpen,
    onOpen: openEditor,
    onClose: closeEditor,
  } = useDisclosure();

  const {
    isOpen: isDealEditorOpen,
    onOpen: openDealEditor,
    onClose: closeDealEditor,
  } = useDisclosure();

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    placingStageCode: "",
    productCode,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const dealsUrl = `https://poc.mrc.ai/xol/api/deals`;
  const productsUrl = `https://poc.mrc.ai/${productCode}/api/product`;

  const loadDeals = async () => {
    try {
      const res = await fetch(dealsUrl);
      const data = await res.json();
      setDeals(Array.isArray(data) ? data : data.deals || []);
    } catch (err) {
      console.error("Failed to load deals:", err);
      toast({ title: "Error loading deals", status: "error" });
    }
  };

  const loadProduct = async () => {
    try {
      const res = await fetch(productsUrl);
      const data = await res.json();
      setProduct(data);
    } catch (err) {
      console.error("Failed to load product:", err);
      toast({ title: "Error loading product", status: "error" });
    }
  };

  useEffect(() => {
    loadDeals();
  }, [productCode]);

  useEffect(() => {
    loadProduct();
  }, []);

  const handleAdd = () => {
    setFormData({ name: "", type: "", placingStageCode: "", productCode });
    setSelected(null);
    onOpen();
  };

  const handleEdit = (deal) => {
    setSelected(deal);
    setFormData({
      name: deal.name || "",
      type: deal.type || "",
      placingStageCode: deal.placingStageCode || "",
      placingTransactionFunctionCode:
        deal.placingTransactionFunctionCode || "",
    });
    onOpen();
  };

  const handleEditDealInstance = (deal) => {
    setSelectedDealInstance(deal);
    openDealEditor();
  };

  const handlePlacementOpen = (deal) => {
    setSelectedGRLCPlacement(deal.grlcPlacement || {});
    openEditor();
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${dealsUrl}/${id}`, { method: "DELETE" });
      toast({ title: "Deal deleted", status: "info" });
      loadDeals();
    } catch (err) {
      toast({ title: "Error deleting deal", status: "error" });
    }
  };

  const handleSubmit = async () => {
    const method = selected ? "PUT" : "POST";
    const url = selected ? `${dealsUrl}/${selected._id}` : dealsUrl;
    const payload = selected ? { ...selected, ...formData } : formData;

    try {
      await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast({
        title: selected ? "Deal updated" : "Deal created",
        status: "success",
      });
      onClose();
      loadDeals();
    } catch (err) {
      toast({ title: "Error saving deal", status: "error" });
    }
  };

  const handleClose = () => {
    navigate(`/Poc`);
  };

  return (
    <Box>
      <Navbar />

      {/* Watermark background */}
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        {/* Overlay content */}
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">
                  {productCode.toUpperCase()} Product Dashboard
                </Heading>
                <Tooltip
                  label="Add Deal"
                  aria-label="Add Deal Tooltip">
                  <IconButton
                    icon={<HiOutlinePlus />}
                    size="md"
                    onClick={handleAdd}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            {clocksLoading ? (
              <Spinner />
            ) : (
              <Wrap spacing={4}>
                {clocks.map((clock) => (
                  <ClockCard key={clock._id} clock={clock} />
                ))}
              </Wrap>
            )}
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            mb={6}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            <Flex mb={6}>
              <HStack spacing={6}>
                <Heading size="md">Deals</Heading>
              </HStack>
            </Flex>

            <SimpleGrid columns={[1, 2, 3, 4, 5]} spacing={6}>
              {deals.map((deal) => (
                <Flex
                  key={deal._id}
                  direction={{ base: "column", md: "row" }}
                  p={5}
                  borderWidth="1px"
                  borderRadius="lg"
                  shadow="md"
                  bg="white">
                  <Box flex="1" pr={{ base: 0, md: 4 }} mb={{ base: 4, md: 0 }}>
                    <HStack
                      align="center"
                      justify="flex-start"
                      spacing={1}>
                      <Tooltip
                        label="Deal Header"
                        aria-label="Edit Deal Header Tooltip">
                        <IconButton
                          icon={<HiOutlineMenu />}
                          size="md"
                          onClick={() =>
                            navigate(
                              `/product/${deal.productCode}/deal/${deal._id}/deal`
                            )
                          }
                          _hover={{ transform: "scale(1.1)" }}
                          transition="transform 0.2s ease"
                        />
                      </Tooltip>

                      <Tooltip
                        label={<UuidPreview uuid={deal._id} />}
                        hasArrow
                        placement="top"
                        openDelay={300}>
                        <Box width="fit-content">
                          <Heading
                            size="md"
                            width="auto"
                            whiteSpace="nowrap"
                            maxW="200px"
                            overflow="hidden"
                            textOverflow="ellipsis">
                            {deal.name}
                          </Heading>
                        </Box>
                      </Tooltip>
                    </HStack>

                    <SimpleGrid columns={5} spacing={4}></SimpleGrid>

                    <SimpleGrid>
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor="gray.200">
                        <Tooltip
                          label="Edit Placement"
                          aria-label="Edit Placement Tooltip">
                          <IconButton
                            icon={<HiOutlineCode />}
                            size="md"
                            onClick={() =>
                              navigate(
                                `/product/${deal.productCode}/deal/${deal._id}/placement`
                              )
                            }
                            _hover={{ transform: "scale(1.1)" }}
                            transition="transform 0.2s ease"
                          />
                        </Tooltip>
                        <Tooltip
                          label="Create Standard Signal"
                          aria-label="Create Standard Signal Tooltip">
                          <IconButton
                            icon={<HiOutlineStatusOnline />}
                            size="md"
                            onClick={() =>
                              navigate(
                                `/product/${deal.productCode}/deal/${deal._id}/signal`
                              )
                            }
                            _hover={{ transform: "scale(1.1)" }}
                            transition="transform 0.2s ease"
                          />
                        </Tooltip>
                      </Box>
                    </SimpleGrid>

                    <SimpleGrid columns={1}>
                      <PlacingStepper
                        size="xs"
                        showSubSteps={false}
                        stageCode={deal.placingStageCode}
                        transactionCode={deal.placingTransactionCode}
                        defaultOrientation="vertical"
                      />
                    </SimpleGrid>

                    <Box display="flex" justifyContent="flex-end">
                      <IconButton
                        icon={<HiOutlineTrash />}
                        size="sm"
                        onClick={() => handleDelete(deal._id)}
                        _hover={{ transform: "scale(1.1)" }}
                        transition="transform 0.2s ease"
                      />
                    </Box>
                  </Box>
                </Flex>
              ))}
            </SimpleGrid>

            <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
              <ModalOverlay />
              <ModalContent
                maxW="600px"
                bg="white"
                borderRadius="xl"
                boxShadow="lg"
                border="1px solid"
                px={6}>
                <ModalHeader fontSize="xl" fontWeight="bold">
                  {selected ? "Edit Deal Header" : "Add Deal Header"}
                </ModalHeader>
                <ModalBody>
                  <Box
                    as="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}>
                    <Stack spacing={4}>
                      <Input
                        placeholder="Deal Name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                    </Stack>
                  </Box>
                </ModalBody>
                <ModalFooter>
                  <ModalCloseButton />
                  <Button onClick={handleSubmit}>
                    {selected ? "Update" : "Create"}
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductHomepage;
